import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const TIERS = [
  { value: "starter", label: "Starter" },
  { value: "plus", label: "Plus" },
  { value: "pro", label: "Pro" },
  { value: "elite", label: "Elite" },
  { value: "agency", label: "Agency" },
  { value: "comp", label: "Complimentary" },
  { value: "admin_workspace", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
];

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId: string;
  onApplied?: () => void;
};

export const TierOverrideDialog = ({ open, onOpenChange, userId, onApplied }: Props) => {
  const [tier, setTier] = useState("starter");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    setBusy(true);
    const { error } = await supabase.functions.invoke("admin-set-tier-override", {
      body: { user_id: userId, tier, reason: reason.trim() },
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Tier override applied");
    setReason("");
    onOpenChange(false);
    onApplied?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply tier override</DialogTitle>
          <DialogDescription>
            Sets <code>profiles.account_tier</code> directly. If the user later changes their Stripe subscription,
            the webhook will overwrite this value. Logged to the audit trail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>New tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Reason (required, min 10 chars)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="e.g. Comped Pro for 3 months — support escalation #1234" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>Apply override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
